import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const STORAGE_KEY = 'floreal_cookie_consent_v1';

/**
 * Cookie consent banner.
 * Persists user choice in localStorage under `floreal_cookie_consent_v1`.
 * Values: 'all' | 'necessary' | { necessary:true, functional:boolean }
 *
 * The site currently only uses NECESSARY cookies (session/httpOnly admin cookie)
 * plus optional FUNCTIONAL data in localStorage (inspirations/likes gallery).
 * Users can disable the functional one at any time via "Gestisci preferenze".
 */
export function readConsent() {
    try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch { return null; }
}

export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [managing, setManaging] = useState(false);
    const [functional, setFunctional] = useState(true);

    useEffect(() => {
        const stored = readConsent();
        if (!stored) setVisible(true);
        else setFunctional(!!stored?.functional);
    }, []);

    const save = (choice) => {
        const payload = {
            necessary: true,
            functional: choice === 'all' ? true : choice === 'necessary' ? false : functional,
            saved_at: new Date().toISOString(),
        };
        try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); }
        catch (e) { console.warn('Impossibile salvare le preferenze cookie (storage non disponibile):', e); }
        setVisible(false);
        setManaging(false);
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-label="Preferenze cookie"
            data-testid="cookie-banner"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[300]"
        >
            <div className="rounded-xl border border-border bg-card shadow-lg p-5 sm:p-6 backdrop-blur">
                <div className="flex items-start gap-3">
                    <Cookie className="h-5 w-5 text-atelier-bronze-soft shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div className="flex-1">
                        <p className="font-serif text-lg leading-tight">Rispettiamo la tua privacy</p>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                            Usiamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso, cookie funzionali per ricordare le tue preferenze (es. le immagini salvate in galleria).
                            {" "}<Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Scopri di più</Link>.
                        </p>

                        {managing && (
                            <div className="mt-4 space-y-3 border-t border-border pt-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-medium">Necessari</p>
                                        <p className="text-xs text-muted-foreground">Indispensabili per il funzionamento (autenticazione admin, sicurezza).</p>
                                    </div>
                                    <Switch checked disabled data-testid="cookie-nec" />
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-medium">Funzionali</p>
                                        <p className="text-xs text-muted-foreground">Ricordano le immagini che salvi in galleria.</p>
                                    </div>
                                    <Switch checked={functional} onCheckedChange={setFunctional} data-testid="cookie-func" />
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                            {!managing ? (
                                <>
                                    <Button size="sm" onClick={() => save('all')} data-testid="cookie-accept-all">Accetta tutti</Button>
                                    <Button size="sm" variant="outline" onClick={() => save('necessary')} data-testid="cookie-reject">Rifiuta non necessari</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setManaging(true)} data-testid="cookie-manage">Gestisci preferenze</Button>
                                </>
                            ) : (
                                <>
                                    <Button size="sm" onClick={() => save('custom')} data-testid="cookie-save-prefs">Salva preferenze</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setManaging(false)}>Indietro</Button>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        aria-label="Chiudi"
                        onClick={() => save('necessary')}
                        className="text-muted-foreground hover:text-foreground p-1 -m-1"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
